import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@auth';
import { AppService } from './app.service';
import { EmbedService } from './embed.service';
import { PortalBridgeService, PortalInboundMessage } from './portal-bridge.service';
import { PortalChromeService } from './portal-chrome.service';
import { SwitchLanguageService } from './switch-language.service';

/**
 * Keeps this app aligned with the Edara Portal that embeds it: organization, language and
 * theme are chosen in the portal's header, and arrive here as `postMessage` commands while the
 * frame stays mounted. Handling them in place — rather than letting the portal remount the
 * iframe on every switch — is what preserves the local Dexie cache, the SignalR connection and
 * the warm PWA bundle.
 *
 * The values the portal passes on the frame URL are NOT applied here: language and theme have
 * to be in effect on the first paint, so [[SwitchLanguageService]] and [[AppService]] read them
 * during bootstrap. Only the organization is reconciled here, once, after boot.
 *
 * Does nothing at all unless [[EmbedService]] reports an embedded session.
 */
@Injectable({ providedIn: 'root' })
export class PortalSyncService {

  private started = false;

  constructor(
    private embedService: EmbedService,
    private bridge: PortalBridgeService,
    private authService: AuthService,
    private appService: AppService,
    private switchLanguageService: SwitchLanguageService,
    private portalChromeService: PortalChromeService,
    private router: Router
  ) { }

  /** Begins mirroring portal state. Safe to call unconditionally and more than once. */
  public start(): void {
    if (!this.embedService.isEmbedded || this.started) return;
    this.started = true;

    this.bridge.messages.subscribe({
      next: (message: PortalInboundMessage) => this.apply(message)
    });

    this.reconcileOrganizationFromUrl();
    this.announceReadiness();
    // Lend the portal this app's header colour and the top-bar actions it has no equivalent for.
    this.portalChromeService.start();
  }

  private apply(message: PortalInboundMessage): void {
    switch (message.type) {
      case 'set-organization':
        this.switchOrganization(message.keycloakOrgId, message.tenantId);
        break;
      case 'set-language':
        this.switchLanguageService.applyLanguage(message.language);
        break;
      case 'set-theme':
        this.appService.changeTheme(message.theme);
        break;
      case 'logout':
        this.authService.clearLocalSession();
        break;
      case 'action':
        this.portalChromeService.runAction(message.action);
        break;
      case 'navigate':
        // The user picked a page in the portal's menu. Already validated as an in-app path.
        this.router.navigateByUrl(message.url);
        break;
      case 'reset':
        // Reopened from the portal. The frame stayed alive while hidden, so return to the landing
        // page rather than leaving the user wherever they were last time. A plain navigation, not
        // a reload — the point is to keep the app warm.
        this.router.navigateByUrl('/');
        break;
    }
  }

  /**
   * The portal hands the active organization over on the frame URL so the very first render is
   * already scoped to it. A switch is only worth its cost — a new token, a wiped local database
   * and a navigation home — when the frame really is on a different tenant, so compare first.
   */
  private reconcileOrganizationFromUrl(): void {
    // `oidcOrg` is the Edara registration tenant id — the portal's shared parameter, meaning the
    // same thing for every product. `keycloakOrgId` is the extra one Edara 3.0 needs, because it
    // switches organization against Keycloak itself instead of through a hand-off page.
    const keycloakOrgId = this.embedService.param('keycloakOrgId');
    const tenantId = this.embedService.param('oidcOrg');
    if (keycloakOrgId) {
      this.switchOrganization(keycloakOrgId, tenantId ?? '');
    }
  }

  /**
   * Switches tenant only on a genuine mismatch. `tenantId` (the Edara registration org id) is the
   * comparison key because it is decoded straight from the access token, whereas the Keycloak id
   * of the active organization depends on the userinfo org list that loads asynchronously after
   * login — comparing against that would fire a spurious switch on every cold boot. With no
   * tenantId to compare, we leave the session alone rather than risk wiping local data.
   */
  private switchOrganization(keycloakOrgId: string, tenantId: string): void {
    if (!this.authService.isAuthenticated()) return;
    if (!tenantId) return;
    if (this.authService.currentTenant === tenantId) return;

    this.authService.switchTenant(keycloakOrgId);
  }

  /**
   * Tells the portal whether this frame has a usable session. Keycloak's login page refuses to be
   * framed, so an unauthenticated frame cannot recover on its own — only the portal can, at top
   * level. See [[authGuard]], which suppresses the in-frame redirect for the same reason.
   */
  private announceReadiness(): void {
    if (this.authService.isAuthenticated()) {
      this.bridge.send({ type: 'ready', organization: this.authService.currentTenant || null });
    } else {
      this.bridge.send({ type: 'auth-required' });
    }

    this.authService.userLoggedIn$.subscribe({
      next: () => this.bridge.send({ type: 'ready', organization: this.authService.currentTenant || null })
    });
  }
}
